/// <amd-module name="Build/Queue-Panel/TemplateParametersView" />
import { TemplateParameterInputType } from "Build/Client/Contracts/DataProvider";
import * as Resources from "Build/Queue-Panel/Resources";
import { QueueBuildStore } from "Build/Queue-Panel/Stores/QueueBuild";
import * as React from "react";
import { ITemplateParameter } from "Types";
import { IObservableArray, IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner, SpinnerOrientation } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

interface ITemplateParametersViewProps {
    className?: string;
    store: QueueBuildStore;
    allowEditing: boolean;
}

export class TemplateParametersView extends React.Component<ITemplateParametersViewProps> {
    private _isLoading: IObservableValue<boolean>;
    private _templateParameters?: IObservableArray<ITemplateParameter>;

    constructor(props: ITemplateParametersViewProps) {
        super(props);
        this._isLoading = new ObservableValue(false);
        this._templateParameters = props.store.templateParameters;
    }

    public componentDidMount() {
        if (this._templateParameters) {
            this._initializeResources();
            this._templateParameters.subscribe(() => this._initializeResources());
        }
    }

    public render(): JSX.Element {
        return (
            <Observer loading={this._isLoading} templateParameters={this._templateParameters}>
                {(props: { loading: boolean; templateParameters: ITemplateParameter[] }) =>
                    props.loading ? (
                        <div className={css(this.props.className, "flex-row justify-start")}>
                            <Spinner orientation={SpinnerOrientation.row} label={Resources.ProcessingYamlMessage} />
                        </div>
                    ) : props.templateParameters && props.templateParameters.length > 0 ? (
                        <div className={css(this.props.className, "flex-column flex-noshrink rhythm-vertical-16")}>
                            {props.templateParameters.map(parameter => (
                                <TemplateParameterInputField
                                    key={parameter.data.name}
                                    allowEditing={this.props.allowEditing}
                                    onValueChanged={this.props.store.updateTemplateParameter}
                                    templateParameter={parameter}
                                />
                            ))}
                        </div>
                    ) : null
                }
            </Observer>
        );
    }

    private _initializeResources = async () => {
        if (!this.props.store.templateParametersAreLoaded) {
            this._isLoading.value = true;
            await this.props.store.fetchPipelineTemplateParameters();
            this._isLoading.value = false;
        }
    };
}

interface ITemplateParameterInputFieldProps {
    /**
     * type that is passed to the <input> element, e.g. password, number, etc.
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types for a full list
     *
     * @default text
     */
    inputType?: "text" | "number";

    /**
     * Template parameter to render
     */
    templateParameter: ITemplateParameter;

    /**
     * Update the value of the template parameter
     *
     * @param newParam updated template parameter
     */
    onValueChanged(newParam: ITemplateParameter): void;

    allowEditing: boolean
}

const TemplateParameterInputField: React.FunctionComponent<ITemplateParameterInputFieldProps> = props => {
    const parameterData = props.templateParameter.data;

    switch (parameterData.type) {
        case TemplateParameterInputType.Checkbox:
            return <ParameterCheckBox {...props} />;
        case TemplateParameterInputType.TextField:
            return <ParameterTextBox {...props} />;
        case TemplateParameterInputType.NumericTextField:
            return <ParameterTextBox {...props} inputType="number" />;
        case TemplateParameterInputType.Dropdown:
            return <ParameterDropdown {...props} />;
        case TemplateParameterInputType.RadioButtonGroup:
            return <ParameterRadioButtonGroup {...props} />;
        case TemplateParameterInputType.MultilineTextField:
        default:
            return <ParameterTextBox {...props} multiline={true} />;
    }
};

export interface ITemplateParameterTextFieldProps extends ITemplateParameterInputFieldProps {
    /**
     * Show text field that accepts multiline input
     * @default false
     */
    multiline?: boolean;
}

const ParameterTextBox: React.FunctionComponent<ITemplateParameterTextFieldProps> = props => {
    const { onValueChanged, templateParameter, multiline = false, inputType = "text" } = props;
    const { data, value, errorMessage } = templateParameter;

    const label = !data.default ? (
        <div className="flex-row">
            <div className="flex-grow">{data.displayName || data.name}</div>
            <div className="secondary-text">{Resources.RequiredLabel}</div>
        </div>
    ) : (
        data.displayName || data.name
    );

    return (
        <FormItem className="flex-stretch flex-noshrink" error={!!errorMessage} label={label} message={errorMessage}>
            <TextField
                autoAdjustHeight={multiline}
                inputType={inputType}
                multiline={multiline}
                disabled={!props.allowEditing}
                onChange={(event, value) => onValueChanged({ data, value, errorMessage })}
                placeholder={data.default}
                value={value}
            />
        </FormItem>
    );
};

const ParameterCheckBox: React.FunctionComponent<ITemplateParameterInputFieldProps> = props => {
    const { onValueChanged, templateParameter } = props;
    const { data, value, errorMessage } = templateParameter;

    const handleChange = (event: any, checked: boolean) => {
        const stringValue = checked ? "true" : "false";
        onValueChanged({ data, value: stringValue, errorMessage });
    };

    return (
        <Checkbox
            checked={value.toLowerCase() === "true"}
            className="flex-stretch flex-noshrink padding-horizontal-0"
            label={data.displayName || data.name}
            onChange={handleChange}
        />
    );
};

const ParameterDropdown: React.FunctionComponent<ITemplateParameterInputFieldProps> = props => {
    const { onValueChanged, templateParameter } = props;
    const { data, value, errorMessage } = templateParameter;

    if (!data.values || data.values.length === 0) {
        return null;
    }

    const options: IListBoxItem<string>[] = data.values.map((value: string) => ({ id: value, text: value }));

    const selection = new DropdownSelection();
    const valueIndex = data.values.findIndex(val => val === value);
    const indexToSelect = valueIndex > -1 ? valueIndex : 0;
    selection.select(indexToSelect);

    const handleChange = (event: any, item: IListBoxItem<string>): void => {
        onValueChanged({ data, value: item.text || "", errorMessage });
    };

    const label = data.displayName || data.name;
    return (
        <FormItem className="flex-stretch flex-noshrink" label={label}>
            <Dropdown ariaLabel={label} items={options} onSelect={handleChange} selection={selection} />
        </FormItem>
    );
};

const ParameterRadioButtonGroup: React.FunctionComponent<ITemplateParameterInputFieldProps> = props => {
    const { onValueChanged, templateParameter } = props;
    const { data, value, errorMessage } = templateParameter;

    if (!data.values || data.values.length === 0) {
        return null;
    }

    const handleChange = (value: string): void => {
        onValueChanged({ data, value: value, errorMessage });
    };

    const selectedRadioButton = value || data.default;
    const label = data.displayName || data.name;
    return (
        <RadioButtonGroup className="flex-noshrink" onSelect={handleChange} selectedButtonId={selectedRadioButton} text={label}>
            {data.values.map((value, index) => (
                <RadioButton key={`radio-button-${data.name}-${index}`} id={value} text={value} />
            ))}
        </RadioButtonGroup>
    );
};
